import _ from 'lodash';
import { stripScripts } from 'ub/utils';
import utils from 'ub/fonts/font-service/utils-new';
import initialState from './states/initial-state';

import sourceMode from 'ub/ui/inline-text/source-mode-components/source-mode.jsx';
import * as DynamicTextAPI from 'ub/ui/inline-text/APIs/dynamic-text-api';
import { selectDynamicTag } from 'ub/ui/inline-text/utils/dtr-selection-manager';
import { recordTextStyle } from 'ub/control/inherit-text-style';

function getElement(elementId) {
  return window.editor.page.getElementById(elementId);
}

const focusIntoTextEditor = element => {
  // froala focus event brings viewport scrollTop to top of
  // editor, we want to maintain scrollTop to prevent jumping
  const currentScrollTop = window.editor.canvasBody.e.scrollTop;
  element.inlineTextStyleAPI.focus();
  window.editor.canvasBody.e.scrollTop = currentScrollTop;

  element.inlineTextStyleAPI.restoreSelection();

  const $selection = element.inlineTextStyleAPI.getSelectedElement();
  selectDynamicTag($selection);
};

export const styleChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.toggleTextStyle(val);
  recordTextStyle(element);

  return {
    ...state,
    textStyles: element.inlineTextStyleAPI.getTextStyles(),
    fontSize: element.inlineTextStyleAPI.getComputedFontSize() || initialState.fontSize,
  };
};

export const alignmentChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setAlignment(val);
  recordTextStyle(element);

  return {
    ...state,
    alignment: element.inlineTextStyleAPI.getAlignment() || initialState.alignment,
  };
};

export const listStyleChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setListStyle(val);

  return {
    ...state,
    listStyle: element.inlineTextStyleAPI.getListStyle(),
    lineHeight: element.inlineTextStyleAPI.getComputedLineHeight() || initialState.lineHeight,
  };
};

export const indentationChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI[val]();

  return state;
};

export const horizontalLineChange = state => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.insertHorizontalLine();

  return state;
};

export const lineHeightChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setLineHeight(val);
  recordTextStyle(element);

  return {
    ...state,
    lineHeight: element.inlineTextStyleAPI.getComputedLineHeight() || initialState.lineHeight,
  };
};

export const fontSizeChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setFontSize(val);
  recordTextStyle(element);

  return {
    ...state,
    fontSize: element.inlineTextStyleAPI.getComputedFontSize() || initialState.fontSize,
  };
};

export const paragraphStyleChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);

  element.inlineTextStyleAPI.setParagraphStyle(val);

  recordTextStyle(element);

  return {
    ...state,
    paragraphStyle: element.inlineTextStyleAPI.getParagraphStyle() || initialState.paragraphStyle,
    alignment: element.inlineTextStyleAPI.getAlignment() || initialState.alignment,
    lineHeight: element.inlineTextStyleAPI.getComputedLineHeight() || initialState.lineHeight,
    textStyles: element.inlineTextStyleAPI.getTextStyles(),
    fontSize: element.inlineTextStyleAPI.getComputedFontSize() || initialState.fontSize,
  };
};

export const fontChange = (state, { family, weight, style }) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);

  element.inlineTextStyleAPI.setFontFamily(family);
  element.inlineTextStyleAPI.setFontWeight(weight);
  element.inlineTextStyleAPI.setFontStyle(style);

  recordTextStyle(element);

  return {
    ...state,
    fontFamily: element.inlineTextStyleAPI.getFontFamily() || initialState.fontFamily,
    fontWeight: element.inlineTextStyleAPI.getFontWeight() || initialState.fontWeight,
    fontStyle: element.inlineTextStyleAPI.getFontStyle() || initialState.fontStyle,
  };
};

export const fontColorChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setColor(val);
  recordTextStyle(element);

  return {
    ...state,
    fontColor: element.inlineTextStyleAPI.getColor() || initialState.fontColor,
  };
};

export const fontBackgroundColorChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setFontBackgroundColor(val);
  recordTextStyle(element);

  return {
    ...state,
    fontBackgroundColor:
      element.inlineTextStyleAPI.getFontBackgroundColor() || initialState.fontBackgroundColor,
  };
};

export const urlChange = (state, url) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);

  if (_.isEmpty(url)) {
    element.inlineTextStyleAPI.removeLink();
  } else {
    const sanitizedUrl = stripScripts(url);
    element.inlineTextStyleAPI.setLink(sanitizedUrl);
  }

  return {
    ...state,
    urlValue: element.inlineTextStyleAPI.getLink(),
  };
};

export const urlTargetChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setLinkTarget(val);

  return {
    ...state,
    target: element.inlineTextStyleAPI.getLinkTarget(),
  };
};

export const urlActionChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setLinkAction(val);

  return {
    ...state,
    urlAction: element.inlineTextStyleAPI.getLinkAction(),
  };
};

export const passUrlParamsChange = (state, val) => {
  const element = getElement(state.elementId);

  focusIntoTextEditor(element);
  element.inlineTextStyleAPI.setPassParams(val);

  return {
    ...state,
    passParams: element.inlineTextStyleAPI.getPassParamsValue(),
  };
};

export const setElement = (state, elementId) =>
  updateCurrentTextStyles({
    ...state,
    elementId,
  });

export const updateCurrentTextStyles = state => {
  const element = getElement(state.elementId);

  if (!element) {
    return state;
  }

  const { inlineTextStyleAPI } = element;

  return {
    ...state,
    sysFonts: utils.sysFonts,
    fontFamily: inlineTextStyleAPI.getFontFamily() || initialState.fontFamily,
    fontWeight: inlineTextStyleAPI.getFontWeight() || initialState.fontWeight,
    fontStyle: inlineTextStyleAPI.getFontStyle() || initialState.fontStyle,
    fontSize: inlineTextStyleAPI.getComputedFontSize() || initialState.fontSize,
    textStyles: inlineTextStyleAPI.getTextStyles(),
    alignment: inlineTextStyleAPI.getAlignment() || initialState.alignment,
    listStyle: inlineTextStyleAPI.getListStyle(),
    lineHeight: inlineTextStyleAPI.getComputedLineHeight() || initialState.lineHeight,
    paragraphStyle: inlineTextStyleAPI.getParagraphStyle() || initialState.paragraphStyle,
    fontColor: inlineTextStyleAPI.getColor() || initialState.fontColor,
    fontBackgroundColor:
      inlineTextStyleAPI.getFontBackgroundColor() || initialState.fontBackgroundColor,
    urlAction: inlineTextStyleAPI.getLinkAction(),
    urlValue: inlineTextStyleAPI.getLink(),
    passParams: inlineTextStyleAPI.getPassParamsValue(),
    target: inlineTextStyleAPI.getLinkTarget(),
  };
};

export const changeTab = (state, selectedTab) => {
  return {
    ...state,
    selectedTab,
  };
};

export const updateSelectedText = (state, newText) => {
  return {
    ...state,
    defaultText: newText,
  };
};

export const toggleSourceMode = state => {
  const element = getElement(state.elementId);

  sourceMode.toggle(element.inlineTextStyleAPI);

  return state;
};

export const openDynamicTextDialog = (state, defaultText) => {
  const element = getElement(state.elementId);
  const dtrTagAttrs = { method: '', parameter: '' };

  DynamicTextAPI.showDialog({
    defaultText,
    dtrTagAttrs,
    changeHandler: val => {
      focusIntoTextEditor(element);

      DynamicTextAPI.insertDynamicText(
        val.defaultText,
        val.attributes.method,
        val.attributes.parameter
      );
    },
  });

  return state;
};

export const removeFont = state => {
  const element = getElement(state.elementId);

  element.inlineTextStyleAPI.sendContentChangedEvent();

  return {
    ...state,
    fontFamily: initialState.fontFamily,
    fontWeight: initialState.fontWeight,
    fontStyle: initialState.fontStyle,
  };
};

export const enterEditMode = state =>
  updateCurrentTextStyles({
    ...state,
    inEditMode: true,
  });

export const exitEditMode = state => ({
  ...state,
  inEditMode: false,
  selectedTab: 'style',
});



// WEBPACK FOOTER //
// ./src/ui/inline-text/text-actions.js